import {
  parse,
  parseISO,
  format,
  setDate,
  setMonth,
  setYear,
  addHours
} from 'date-fns';
import { getTimezoneOffset } from 'date-fns-tz';

export function firestoreTimestampToDate(timestamp) {
  if (!timestamp) return '';
  return format(timestamp.toDate(), 'MMMM d, yyyy');
}

export function formatDate(dateString) {
  if (!dateString) return '';
  const d = parseISO(dateString);
  return format(d, 'EEEE, MMMM d, yyyy');
}

export function createDateFromDateString(meetingDate, dateString) {
  let newDate = new Date(meetingDate.getTime());
  let parts = dateString.split('-');
  newDate = setYear(newDate, parts[0]);
  // dateFns months seems to be offset by 1, so jan=0, feb=1, etc. so we subtract 1 from the actual month number.
  newDate = setMonth(newDate, parts[1] - 1);
  newDate = setDate(newDate, parts[2]);
  return newDate;
}

export function convertDateToFormattedUTC(date) {
  let utcDate = addHours(date, date.getTimezoneOffset() / 60);
  return format(utcDate, "yyyyMMdd'T'HHmm00") + 'Z';
}

/**
 *
 *
 * @param {string} meetingDate The meeting date in the format "yyyy-MM-dd".
 * @param {string} timeString The meeting time in the format "hh:mm aaaaa'.m.'". Like "12:00 p.m.".
 * @param {string} timezoneName The timezone name. Like "America/New_York" or "America/Chicago".
 *
 * @returns {date} A date object representing the meeting date and time.
 */
export function createDateFromTimeString(
  meetingDate,
  timeString,
  timezoneName
) {
  const format = "yyyy-MM-dd hh:mm aaaaa'm' XXXX";
  const usedTimezoneName = timezoneName || 'US/Eastern'; // Default to Eastern if no timezone is provided.

  // This is a little hacky. We need to convert the offset number into the format date-fns expects.
  // This will probably break if we introduce timezones with 10+ hours offset, but no US timezones do which is all we currently support.
  const offset = `${getTimezoneOffset(usedTimezoneName) / 36000}`.replace(
    '-',
    '-0'
  );

  return parse(`${meetingDate} ${timeString} ${offset}`, format, new Date());
}

export function sortTimeSlots(a, b) {
  const aTime = getMilitaryTime(a.start_time);
  const bTime = getMilitaryTime(b.start_time);

  if (aTime < bTime) {
    return -1;
  }
  if (aTime > bTime) {
    return 1;
  }
  return 0;
}

export function convertToKebabCase(string) {
  return string.replace(/\s+/g, '-').toLowerCase();
}

function getMilitaryTime(t) {
  var arr = t.split(':');
  if (t.indexOf('pm') > -1 && arr[0] !== '12') {
    arr[0] = parseInt(arr[0]) + 12;
  }
  return arr.join('').substring(0, 4);
}

export function getCompanySuiteName(suiteName, companyName) {
  // returns a formatted name like "companyName (suiteName)", if necessary.
  if (!companyName && !suiteName) {
    return '';
  }
  if (companyName && !suiteName) {
    return companyName;
  }
  if (!companyName && suiteName) {
    return suiteName;
  }
  if (companyName === suiteName) {
    return companyName;
  }
  return `${companyName} (${suiteName})`;
}

export function getTimeslot(timeslotId, appointmentSlots) {
  let timeslot;
  Object.keys(appointmentSlots).forEach(date => {
    appointmentSlots[date].some(slot => {
      if (slot.id === timeslotId) {
        timeslot = slot;
        return true;
      }
      return false;
    });
  });
  return timeslot;
}

export function isTimeslotOpen(timeslotId, appointmentSlots) {
  if (!Object.keys(appointmentSlots).length) {
    return true;
  }
  const timeslot = getTimeslot(timeslotId, appointmentSlots);
  return !!timeslot.is_open;
}

export function fixHttpProtocol(url) {
  if (url && url.startsWith('http:')) {
    return url.slice(5);
  }
  return url;
}

function addToGoogleCalendar(calendarData) {
  let utcStart = convertDateToFormattedUTC(calendarData.start);
  let utcEnd = convertDateToFormattedUTC(calendarData.end);
  window.open(
    `http://www.google.com/calendar/event?action=TEMPLATE&dates=${utcStart}%2f${utcEnd}&text=${encodeURIComponent(
      calendarData.description
    )}&location=${encodeURIComponent(
      calendarData.location
    )}&details=${encodeURIComponent(calendarData.notes)}`,
    '_blank'
  );
}

function addToOutlookCalendar(calendarData) {
  calendarData.isOutlook = true;
  addToICalendar(calendarData);
}

function addToICalendar(calendarData) {
  let utcStart = convertDateToFormattedUTC(calendarData.start);
  let utcEnd = convertDateToFormattedUTC(calendarData.end);
  let url = `${
    process.env.FIREBASE_FUNCTIONS_DOMAIN
  }/getICalendar?start=${utcStart}&end=${utcEnd}&description=${encodeURIComponent(
    calendarData.description
  )}&location=${encodeURIComponent(
    calendarData.location
  )}&notes=${encodeURIComponent(calendarData.notes)}`;
  if (calendarData.isOutlook) {
    url += '&isOutlook=true';
  }
  window.location.href = url;
}

function addToYahooCalendar(calendarData) {
  let utcStart = convertDateToFormattedUTC(calendarData.start);

  // Yahoo takes a duration argument as HHMM, so we do more date gymnastics to calculate that
  let diffMinutes = String(
    Math.floor(((calendarData.end - calendarData.start) / 60000) % 60)
  );
  let diffHours = String(
    Math.floor((calendarData.end - calendarData.start) / 3600000)
  );
  diffMinutes = diffMinutes.length < 2 ? '0' + diffMinutes : diffMinutes;
  if (diffHours.length < 2) {
    diffHours = '0' + diffHours;
  } else if (diffHours.length > 2) {
    diffHours = '99';
  }
  let diffStr = diffHours + diffMinutes;

  window.open(
    `http://calendar.yahoo.com/?v=60&TITLE=${encodeURIComponent(
      calendarData.description
    )}&ST=${utcStart}&DUR=${diffStr}&in_loc=${encodeURIComponent(
      calendarData.location
    )}&DESC=${encodeURIComponent(calendarData.notes)}`,
    '_blank'
  );
}

export function addToCalendar(calendarData) {
  if (calendarData.type === 'google') {
    addToGoogleCalendar(calendarData);
  } else if (calendarData.type === 'ical') {
    addToICalendar(calendarData);
  } else if (calendarData.type === 'yahoo') {
    addToYahooCalendar(calendarData);
  } else if (calendarData.type === 'outlook') {
    addToOutlookCalendar(calendarData);
  }
}

/**
 * Converts the long/Maxx version of timezone names to short display ones for the schedule.
 *
 * @param {string} timeZone A long timezone name like "US/Central".
 * @returns {string} A short timezone name like "CDT" or "CST".
 */
export function convertMeetingTimezoneToShortName(timeZone) {
  return Intl.DateTimeFormat('en-EN', {
    timeZoneName: 'short',
    timeZone: timeZone
  })
    .format(Date.now())
    .split(',')[1]
    .trim();
}



// WEBPACK FOOTER //
// ./src/services/utils.js